import React, { useEffect, useState, useRef } from "react";
import { FileText, Upload, Camera, Search, Trash2, Eye, FileImage } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

const DOC_TYPES = ["Clinical Notes", "Intake & Assessments", "Treatment Plans", "Safety Plans",
  "Billing & Insurance", "Releases & Consents", "Legal Documents", "Referrals",
  "Scanned Records", "Insurance Card", "Lab Result", "Other"];

export default function Documents() {
  const [clients, setClients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState({ client_id: "", doc_type: "", q: "" });
  const [uploadModal, setUploadModal] = useState(false);
  const [scanModal, setScanModal] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => Promise.all([
    api.get("/clients").then(({ data }) => setClients(data)),
    loadDocs(),
  ]);

  const loadDocs = () => {
    const params = new URLSearchParams();
    if (filter.client_id) params.set("client_id", filter.client_id);
    if (filter.doc_type) params.set("doc_type", filter.doc_type);
    if (filter.q) params.set("q", filter.q);
    return api.get(`/documents?${params}`).then(({ data }) => setDocs(data));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadDocs(); }, [filter.client_id, filter.doc_type]);

  const view = async (id) => {
    const { data } = await api.get(`/documents/${id}`);
    setViewing(data);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Deleted");
    loadDocs();
  };

  return (
    <Page
      title="Document Vault"
      subtitle="Encrypted, searchable, HIPAA-ready clinical document library."
      actions={
        <div className="flex gap-2">
          <button onClick={() => setScanModal(true)} className="px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2" style={{ background: "var(--mf-accent)", color: "#0F1B2D" }} data-testid="scan-btn">
            <Camera className="w-4 h-4" /> Scan
          </button>
          <PrimaryButton onClick={() => setUploadModal(true)} testid="upload-doc-btn"><span className="flex items-center gap-2"><Upload className="w-4 h-4" /> Upload</span></PrimaryButton>
        </div>
      }
    >
      <Card delay={0.05} className="mb-5">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--mf-text-muted)" }} />
            <input
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              onBlur={loadDocs}
              onKeyDown={(e) => e.key === "Enter" && loadDocs()}
              placeholder="Search filename, OCR text, tags…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl outline-none text-sm"
              style={{ background: "var(--mf-muted)" }}
              data-testid="doc-search"
            />
          </div>
          <select value={filter.client_id} onChange={(e) => setFilter({ ...filter, client_id: e.target.value })} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="doc-client-filter">
            <option value="">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </select>
          <select value={filter.doc_type} onChange={(e) => setFilter({ ...filter, doc_type: e.target.value })} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="doc-type-filter">
            <option value="">All types</option>
            {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </Card>

      {docs.length === 0 ? <EmptyState icon={FileText} title="No documents yet" description="Upload a file or scan a paper chart to start your vault." /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d, i) => {
            const client = clients.find(c => c.id === d.client_id);
            return (
              <Card key={d.id} delay={Math.min(i*0.04, 0.3)} testid={`doc-${d.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(26,107,114,0.1)" }}>
                    {d.mime?.includes("image") ? <FileImage className="w-5 h-5" style={{ color: "var(--mf-primary)" }} /> : <FileText className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />}
                  </div>
                  <Pill variant="primary">{d.doc_type}</Pill>
                </div>
                <div className="font-medium text-sm mb-1 truncate">{d.filename}</div>
                <div className="text-xs mb-3" style={{ color: "var(--mf-text-muted)" }}>
                  {client ? `${client.first_name} ${client.last_name}` : "—"} · {Math.round(d.size_bytes/1024)} KB · {new Date(d.created_at).toLocaleDateString()}
                </div>
                {d.source && <div className="text-xs mb-3"><Pill>{d.source}</Pill></div>}
                <div className="flex gap-1">
                  <button onClick={() => view(d.id)} className="text-xs px-3 py-1.5 rounded-full flex items-center gap-1" style={{ background: "var(--mf-muted)" }} data-testid={`view-${d.id}`}><Eye className="w-3 h-3" /> View</button>
                  <button onClick={() => remove(d.id)} className="text-xs px-3 py-1.5 rounded-full flex items-center gap-1" style={{ background: "rgba(180,82,83,0.1)", color: "#b91c1c" }} data-testid={`del-${d.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {uploadModal && <UploadModal clients={clients} onClose={() => setUploadModal(false)} onDone={() => { setUploadModal(false); loadDocs(); }} />}
      {scanModal && <ScannerModal clients={clients} onClose={() => setScanModal(false)} onDone={() => { setScanModal(false); loadDocs(); }} />}
      {viewing && <DocViewer doc={viewing} onClose={() => setViewing(null)} />}
    </Page>
  );
}

function UploadModal({ clients, onClose, onDone }) {
  const fileRef = useRef();
  const [client_id, setClientId] = useState("");
  const [doc_type, setDocType] = useState("Other");
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !client_id) return toast.error("Client and file required");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", client_id);
    fd.append("doc_type", doc_type);
    fd.append("source", "uploaded");
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onDone();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="mf-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-serif text-2xl mb-4">Upload Document</h2>
        <form onSubmit={submit} className="space-y-3">
          <select required value={client_id} onChange={(e) => setClientId(e.target.value)} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="upload-client">
            <option value="">Select client…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </select>
          <select value={doc_type} onChange={(e) => setDocType(e.target.value)} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="upload-type">
            {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <input ref={fileRef} type="file" required className="w-full text-sm" data-testid="upload-file" />
          <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Max 10 MB. Supports PDF, JPG, PNG, DOCX.</div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-full">Cancel</button>
            <PrimaryButton type="submit" disabled={uploading} testid="upload-submit">{uploading ? "Uploading…" : "Upload"}</PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function ScannerModal({ clients, onClose, onDone }) {
  const videoRef = useRef();
  const canvasRef = useRef();
  const [stream, setStream] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [client_id, setClientId] = useState("");
  const [doc_type, setDocType] = useState("Scanned Records");
  const [uploading, setUploading] = useState(false);

  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (err) {
      toast.error("Camera not available — use upload instead");
    }
  };

  useEffect(() => {
    start();
    return () => { stream?.getTracks().forEach(t => t.stop()); };
    // eslint-disable-next-line
  }, []);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0);
    c.toBlob((blob) => {
      setCapturedBlob(blob);
      stream?.getTracks().forEach(t => t.stop());
    }, "image/jpeg", 0.92);
  };

  const upload = async () => {
    if (!capturedBlob || !client_id) return toast.error("Client required");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", new File([capturedBlob], `scan_${Date.now()}.jpg`, { type: "image/jpeg" }));
    fd.append("client_id", client_id);
    fd.append("doc_type", doc_type);
    fd.append("source", "scanned");
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Scanned and filed");
      onDone();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="mf-card w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-serif text-2xl mb-4">Document Scanner</h2>
        <div className="text-xs mb-3" style={{ color: "var(--mf-text-muted)" }}>* OCR is mocked until Cloud Vision/Textract API keys are provided.</div>
        {!capturedBlob ? (
          <div>
            <div className="bg-black rounded-2xl overflow-hidden mb-3 aspect-[3/4] flex items-center justify-center">
              <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" data-testid="scanner-video" />
            </div>
            <canvas ref={canvasRef} className="hidden"></canvas>
            <PrimaryButton onClick={capture} testid="capture-btn">Capture Page</PrimaryButton>
          </div>
        ) : (
          <div>
            <img src={URL.createObjectURL(capturedBlob)} alt="Captured" className="w-full rounded-2xl mb-3" data-testid="scan-preview" />
            <div className="space-y-2 mb-3">
              <select value={client_id} onChange={(e) => setClientId(e.target.value)} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="scan-client">
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <select value={doc_type} onChange={(e) => setDocType(e.target.value)} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="scan-type">
                {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setCapturedBlob(null); start(); }} className="px-4 py-2 text-sm rounded-full" style={{ background: "var(--mf-muted)" }} data-testid="rescan-btn">Retake</button>
              <PrimaryButton onClick={upload} disabled={uploading} testid="scan-save-btn">{uploading ? "Saving…" : "Save to Vault"}</PrimaryButton>
            </div>
          </div>
        )}
        <button onClick={onClose} className="mt-3 text-sm w-full" style={{ color: "var(--mf-text-muted)" }}>Cancel</button>
      </div>
    </div>
  );
}

function DocViewer({ doc, onClose }) {
  const isImage = doc.mime?.includes("image");
  const isPDF = doc.mime?.includes("pdf");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="mf-card w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl">{doc.filename}</h2>
          <button onClick={onClose} className="text-sm">Close</button>
        </div>
        <div className="flex items-center gap-2 mb-4 text-xs" style={{ color: "var(--mf-text-muted)" }}>
          <Pill variant="primary">{doc.doc_type}</Pill>
          <span>{Math.round(doc.size_bytes/1024)} KB</span>
          <span>{new Date(doc.created_at).toLocaleString()}</span>
        </div>
        {isImage && <img src={`data:${doc.mime};base64,${doc.content_base64}`} alt={doc.filename} className="w-full rounded-xl" />}
        {isPDF && <iframe src={`data:application/pdf;base64,${doc.content_base64}`} title={doc.filename} className="w-full h-[600px] rounded-xl"></iframe>}
        {!isImage && !isPDF && <div className="p-4 rounded-xl text-sm" style={{ background: "var(--mf-muted)" }}>Preview not available. <a href={`data:${doc.mime};base64,${doc.content_base64}`} download={doc.filename} className="underline" style={{ color: "var(--mf-primary)" }}>Download</a></div>}
        {doc.ocr_text && (
          <div className="mt-4">
            <div className="text-xs uppercase mb-2" style={{ color: "var(--mf-text-muted)" }}>OCR Text</div>
            <div className="p-3 rounded-xl text-sm" style={{ background: "var(--mf-muted)" }}>{doc.ocr_text}</div>
          </div>
        )}
      </div>
    </div>
  );
}
